import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Query, useQuery } from 'react-apollo';
import { useMutation } from '@apollo/react-hooks';
import MaterialTable from 'material-table';
import Spin from 'components/common/Spin';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Select from 'components/form/Select';
import Button from 'components/common/Button';
import { UPDATE_USER } from 'graphql/admin/users';
import { GET_FA_USERS } from 'graphql/admin/coaches';
import { UPDATE_PROFILE } from 'graphql/lc/profile';
import { UPSERT_SETTINGS } from 'graphql/admin/settings';

import Helmet from 'react-helmet';
import { resetPassword } from 'utils/admin.service';
import useSnack from 'hooks/useSnack';
import Typography from '@material-ui/core/Typography';

const AdminFACounselors = (props) => {
  const snack = useSnack();
  const user = useSelector((s) => s.user.user);
  const [updateUser] = useMutation(UPDATE_USER);
  const [updateProfile] = useMutation(UPDATE_PROFILE);
  const [updateSettings] = useMutation(UPSERT_SETTINGS);
  const [defaultCoach, setDefaultCoach] = useState(null);

  const columns = [
    { title: 'User Id', field: 'user_id', type: 'numeric', editable: 'never' },
    { title: 'Status', field: 'user_status', editable: 'never' },
    { title: 'Available', field: 'fc_profiles.0.available', type: 'boolean' },
    { title: 'Username', field: 'user_cognito_username', editable: 'never' },
    { title: 'First Name', field: 'user_first_name', editable: 'never' },
    { title: 'Last Name', field: 'user_last_name', editable: 'never' },
    { title: 'User Email', field: 'user_email', editable: 'never' },
    { title: 'Phone', field: 'user_phone', editable: 'never' },
  ];

  const onResetPassword = async (e, data) => {
    if (window.confirm('Are you sure?')) {
      return resetPassword(data.user_cognito_username)
        .then(() => snack.success('Password updated.'))
        .catch((e) => snack.error(e.message));
    }
  };

  return (
    <Query query={GET_FA_USERS} fetchPolicy={'cache-and-network'} onCompleted={(resp) => {
      if (resp.settings) {
        setDefaultCoach(resp.settings.value);
      }
    }}>
      {({ data, refetch }) => {
        if (!data) return <Spin spinning linear />;

        const onRowUpdate = async (data, old) => {
          const available = data.fc_profiles[0].available;
          if (available !== old.fc_profiles[0].available) {
            await updateProfile({
              variables: {
                id: data.user_id,
                input: {
                  available,
                },
              },
            });
            await refetch();
          }
        };
        const onDelete = (data) => {
          return updateUser({
            variables: {
              userId: data.user_id,
              data: {
                deleted: true,
                deleted_by: user.id,
                deleted_at: new Date().toJSON(),
              },
            },
          })
            .then(() => refetch())
            .catch((e) => snack.error(e.message));
        };

        const onUpdateCoach = async () => {
          await updateSettings({
            variables: {
              key: 'DEFAULT_FA_ID',
              value: defaultCoach || '',
              isPublic: true,
            }
          });
          snack.success('Default coach updated.');
        }

        return (
          <React.Fragment>
            <Helmet title="Financial Aid Counselors" />
            <MaterialTable
              title="Financial Aid Counselors"
              columns={columns}
              data={data.users}
              editable={{
                onRowDelete: onDelete,
                onRowUpdate: onRowUpdate,
                isEditable: (rowData => rowData.user_status === 'confirmed'),
              }}
              actions={[
                {
                  icon: 'visibility',
                  tooltip: 'View',
                  onClick: (event, rowData) =>
                    props.history.push(`/fas/${rowData.user_id}/profile`),
                },
                {
                  icon: 'lock',
                  tooltip: 'Reset password',
                  onClick: onResetPassword,
                },
              ]}
              options={{
                pageSize: 10,
              }}
            />
            <Paper mt={2} component={Box}>
              <Box p={2}>
                <Typography paragraph variant="subtitle1">
                  Default Coach
                </Typography>
                <Select
                  transformValue
                  isClearable
                  isMulti={false}
                  value={defaultCoach}
                  onChange={setDefaultCoach}
                  label="Select default coach"
                  options={data.users.map((i) => ({
                    label: i.user_first_name + ' ' + i.user_last_name,
                    value: i.user_id,
                  }))}
                />
                <Button variant="contained" color="primary" onClick={onUpdateCoach}>
                  Save
                </Button>
              </Box>
            </Paper>
          </React.Fragment>
        );
      }}
    </Query>
  );
};

export default AdminFACounselors;
